/**
 * Forms: a field, a box to tick, and the two kinds of message a form shows.
 *
 * A public form is short — an address, a name, a consent — and every one of these is drawn for a
 * stranger on a phone: the label is always there (a placeholder is not a label), the keyboard is the
 * right one (`type="email"` with `inputMode`), autofill is pointed at the right field, and the tap
 * target is at least 44px tall.
 */
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';

import { cx, mod } from './cx';
import { Icon } from './media';

/**
 * A real `<form>`, so Enter submits from any field — a page whose only action needs a mouse is a page
 * a keyboard cannot finish. The default submission is always prevented: these pages post JSON.
 */
export function Form({
  children,
  onSubmit,
  label,
}: {
  children?: ReactNode | undefined;
  onSubmit: () => void;
  label?: string | undefined;
}) {
  return (
    <form
      className="a-form"
      {...(label ? { 'aria-label': label } : {})}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}

/**
 * One labelled text field.
 *
 * `shape="pill"` with `icon="search"` is a shop's search; `hideLabel` keeps the label for a screen
 * reader where the icon and the placeholder already say it to the eye. `help` is the line under the
 * field — what happens to the address, or that it may be changed.
 */
export function Field({
  label,
  value,
  onChange,
  type = 'text',
  name,
  placeholder,
  autoComplete,
  required,
  disabled,
  help,
  hideLabel,
  shape = 'box',
  icon,
  rows,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: 'text' | 'email' | 'search' | undefined;
  name?: string | undefined;
  placeholder?: string | undefined;
  autoComplete?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  help?: ReactNode | undefined;
  hideLabel?: boolean | undefined;
  shape?: 'box' | 'pill' | undefined;
  icon?: 'search' | undefined;
  /** A multi-line field of this many rows — a learner's answer, a note. */
  rows?: number | undefined;
}) {
  const helpId = useId();
  return (
    <label className={cx('a-field', mod('a-field', 'shape', shape), icon && 'a-field--icon')}>
      <span className={hideLabel ? 'a-visually-hidden' : 'a-field__label'}>{label}</span>
      <span className="a-field__control">
        {icon ? (
          <span className="a-field__icon" aria-hidden="true">
            <Icon name={icon} size="sm" />
          </span>
        ) : null}
        {rows ? (
          <textarea
            className="a-field__input a-field__input--multiline"
            name={name}
            value={value}
            rows={rows}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            aria-describedby={help ? helpId : undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <input
            className="a-field__input"
            name={name}
            type={type}
            value={value}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            inputMode={type === 'email' ? 'email' : type === 'search' ? 'search' : undefined}
            aria-describedby={help ? helpId : undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </span>
      {help ? (
        <span id={helpId} className="a-field__help">
          {help}
        </span>
      ) : null}
    </label>
  );
}

/**
 * A title somebody edits in place — the heading of a `Modal` that creates a thing (2026-10-19,
 * owner: *"the input with the New Game title, editable, without any label, so that the user is
 * invited to change it because it is automatically selected when the modal opens"*).
 *
 * Drawn as the heading it will become: display type, no box, a hairline under it only while it has
 * the focus. The label is for a screen reader alone. `select` focuses it and selects the whole
 * value when it mounts, so the first key typed replaces the suggestion.
 */
export function TitleField({
  label,
  value,
  onChange,
  select,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  select?: boolean | undefined;
}) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!select) return;
    input.current?.focus();
    input.current?.select();
  }, [select]);
  return (
    <input
      ref={input}
      className="a-title-field"
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/**
 * A box to tick, with its sentence beside it. The sentence may hold a link (the terms), and `hint`
 * says in words whether it is required or optional — never an asterisk.
 *
 * Never pre-ticked by anubis: an acceptance is a decision about this visit, and a ticked box nobody
 * ticked is not a decision at all.
 */
export function Check({
  checked,
  onChange,
  children,
  hint,
  required,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children?: ReactNode | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
}) {
  const hintId = useId();
  return (
    <label className="a-check">
      <input
        className="a-check__box"
        type="checkbox"
        checked={checked}
        required={required}
        aria-describedby={hint ? hintId : undefined}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="a-check__text">
        {children}
        {hint ? (
          <>
            {' '}
            <span id={hintId} className="a-check__hint">
              ({hint})
            </span>
          </>
        ) : null}
      </span>
    </label>
  );
}

/**
 * Something went wrong, in the API's own words — written to be read by the person, so shown verbatim.
 * `role="alert"`, so a screen reader hears it without going looking. `boxed` draws it as a panel
 * inside a form; bare, it is one line under a field.
 */
export function Alert({ children, boxed }: { children?: ReactNode; boxed?: boolean }) {
  return (
    <p role="alert" className={cx('a-alert', boxed && 'a-alert--boxed')}>
      {children}
    </p>
  );
}

/**
 * An empty slot a THIRD PARTY draws itself into — a payment gateway's card form (Stripe's card
 * element, Mercado Pago's card brick), 2026-10-10. Only an `id`, which is what those SDKs mount on.
 * An app may not write raw markup, and it must never draw card fields itself (that would put it in
 * PCI scope), so this is the one way a gateway draws inside an anubis page. Full width; the widget
 * sets its own height, and the slot gives it a field's bordered box.
 */
export function Mount({ id }: { id: string }) {
  return <div id={id} className="a-mount" />;
}

/** A note in the accent's tint — "this one is free", "only twelve left". Information, not a warning. */
export function Notice({ children }: { children?: ReactNode }) {
  return <p className="a-notice">{children}</p>;
}

/**
 * Something is happening that the person is waiting on — signing in, paying, redirecting. A turning
 * ring in the accent beside the sentence, announced politely. Not a `Skeleton`: a skeleton stands in
 * for content that is about to appear, and here nothing is; the page would look frozen (2026-10-10,
 * the sign-in page after Google answered).
 *
 * `label` instead of children draws the ring alone, with the sentence for screen readers only — the
 * sign-in page since 2026-10-18 (the owner: "I just want to see the spinner, not the text").
 */
export function Working({ children, label }: { children?: ReactNode; label?: string }) {
  return (
    <p className="a-working" role="status" aria-live="polite">
      <span className="a-working__ring" aria-hidden="true" />
      {label !== undefined && children === undefined ? (
        <span className="a-visually-hidden">{label}</span>
      ) : (
        <span>{children}</span>
      )}
    </p>
  );
}

/**
 * A bare turning ring in the colour of the words around it — inside a button while its request is in
 * flight (2026-10-17: free's CTA, the owner: "a spinner in the colour of the button's text" instead
 * of "Sending…"). `label` is what a screen reader hears in place of the words it replaced.
 */
export function Spinner({ label }: { label: string }) {
  return <span className="a-spinner" role="status" aria-label={label} />;
}

/**
 * Choosing files to send — a learner's hand-in. A real file input under a label that looks like the
 * page's quiet action, so a keyboard and a screen reader reach it like any control; `accept` and
 * `maxBytes` are checked here so a refused file is named before anything is uploaded (the API checks
 * again). `onPick` receives each accepted file; `onRefuse` each refused one.
 */
export function FilePicker({
  label,
  accept,
  maxBytes,
  multiple = true,
  disabled,
  onPick,
  onRefuse,
}: {
  label: string;
  /** File-name pattern a file must match, e.g. `/\.(md|txt)$/i`. Names, because a browser reports
   *  `.md` with no MIME type at all. */
  accept?: RegExp | undefined;
  maxBytes?: number | undefined;
  multiple?: boolean | undefined;
  disabled?: boolean | undefined;
  onPick: (file: File) => void;
  onRefuse?: ((file: File) => void) | undefined;
}) {
  return (
    <label className={cx('a-file', disabled && 'a-file--disabled')}>
      <input
        className="a-visually-hidden"
        type="file"
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          for (const file of Array.from(event.target.files ?? [])) {
            const ok = (!accept || accept.test(file.name)) && (!maxBytes || file.size <= maxBytes);
            if (ok) onPick(file);
            else onRefuse?.(file);
          }
          event.target.value = '';
        }}
      />
      <span className="a-file__label">{label}</span>
    </label>
  );
}

/**
 * The one place a destructive act lives in a form, GitHub's way (owner, 2026-10-21: *"the delete
 * button must never be next to a save or confirm button"*): a box with a danger hairline at the
 * foot of the form's body — a title, a sentence of what is lost, and ONE button in the danger
 * colour — never in the row of the form's own acts. The button usually opens a `Confirm`.
 */
export function DangerZone({
  title,
  detail,
  act,
  onAct,
}: {
  /** "Danger zone". */
  title: string;
  /** What pressing it loses: "Deleting a game removes its chat, pictures and films." */
  detail?: ReactNode | undefined;
  /** The button's word: "Delete". */
  act: string;
  onAct: () => void;
}) {
  return (
    <section className="a-danger" aria-label={title}>
      <div className="a-danger__words">
        <p className="a-danger__title">{title}</p>
        {detail ? <p className="a-danger__detail">{detail}</p> : null}
      </div>
      <button type="button" className="a-danger__act" onClick={onAct}>
        {act}
      </button>
    </section>
  );
}

/**
 * The offline queue, as one glyph in a `Bar` (2026-10-21) — anubis' drawing of what ra's
 * `useOutboxStatus` says, as isis' `OutboxStatus` draws it in a console. Nothing when there is
 * nothing to say (`idle`); a quiet turning ring while changes wait (still while offline); a danger
 * glyph for changes that did not go through, which a press retries (the hook also retries by itself);
 * a short line when this browser cannot keep changes at all. The words are the app's, in its
 * language.
 */
export function SyncStatus({
  kind,
  count,
  online,
  onRetry,
  labels,
}: {
  kind: 'off' | 'stuck' | 'pending' | 'idle';
  count: number;
  online: boolean;
  onRetry: () => void;
  labels: {
    /** "Offline saving is off in this browser". */
    off: string;
    /** "2 changes not saved — press to retry". */
    stuck: (count: number) => string;
    /** "Saving 2". */
    syncing: (count: number) => string;
    /** "2 waiting for a connection". */
    waiting: (count: number) => string;
  };
}) {
  if (kind === 'idle') return null;
  if (kind === 'off') {
    return (
      <span className="a-sync a-sync--off" role="status">
        <Icon name="warning" size="sm" />
        <span>{labels.off}</span>
      </span>
    );
  }
  if (kind === 'stuck') {
    const label = labels.stuck(count);
    return (
      <button
        type="button"
        className="a-sync a-sync--stuck"
        aria-label={label}
        title={label}
        onClick={onRetry}
      >
        <Icon name="warning" size="sm" />
      </button>
    );
  }
  const label = online ? labels.syncing(count) : labels.waiting(count);
  return (
    <span
      className={cx('a-sync', 'a-sync--pending', !online && 'a-sync--waiting')}
      role="status"
      aria-label={label}
      title={label}
    >
      <span className="a-sync__ring" aria-hidden="true" />
    </span>
  );
}
